/**
 * Configurações gerais da plataforma: os e-mails e pushes que o sistema
 * manda sozinho (transação, convite, expiração…) e que hoje vivem como HTML
 * e texto fixos em `legacy/fixe-api`. O master edita aqui; o backend lê
 * daqui com o texto do legado como reserva.
 */

export type PlatformLang = 'pt' | 'es' | 'en';

export const PLATFORM_LANGS: Array<{ value: PlatformLang; label: string }> = [
  { value: 'pt', label: 'PT' },
  { value: 'es', label: 'ES' },
  { value: 'en', label: 'EN' },
];

/** O idioma vem do passbook (`hasES` / `hasEN`); PT é o padrão. */
export type LocalizedText = Record<PlatformLang, string>;

export type SystemEmailKind = 'transaction' | 'invite' | 'app-code' | 'android-card' | 'export';

/**
 * Bloco "responder pesquisa" do e-mail de transação. Só entra nas lojas com
 * pesquisa transacional (`regraCartao.hasSurvey`); no template fica como
 * `{{bloco_pesquisa}}`, no ponto que o master quiser.
 */
export interface SurveyBlockConfig {
  format: 'text' | 'banner';
  /** Texto antes do link (formato texto). */
  message: string;
  linkLabel: string;
  /** Imagem clicável (formato banner). */
  bannerUrl: string;
  bannerAlt: string;
}

export interface SystemEmail {
  kind: SystemEmailKind;
  label: string;
  /** Quando sai. Uma linha. */
  trigger: string;
  subject: LocalizedText;
  /** Template da plataforma. `null` = HTML fixo do legado. */
  templateId: string | null;
  /** Variáveis que o backend substitui no assunto e no template. */
  variables: string[];
  /** Só na transação: como o `{{bloco_pesquisa}}` é montado. */
  surveyBlock?: SurveyBlockConfig;
  enabled: boolean;
  /** Função do legado que envia hoje. */
  legacy: string;
}

export type SystemPushKind =
  | 'points-added'
  | 'stamps-added'
  | 'redeem'
  | 'purchase-cancelled'
  | 'profile-updated'
  | 'expiring-soon'
  | 'expired'
  | 'nps-bonus'
  | 'mgm-reward'
  | 'coupon';

export interface SystemPushRule {
  /** Avisar N dias antes (expiração). */
  daysBefore?: number;
}

export interface SystemPush {
  kind: SystemPushKind;
  label: string;
  trigger: string;
  /** Título do push. `{{loja}}` vira "Loja - Unidade". */
  title: LocalizedText;
  body: LocalizedText;
  variables: string[];
  rule?: SystemPushRule;
  enabled: boolean;
  legacy: string;
}

export interface PartnerSender {
  partner: string;
  fromEmail: string;
  fromName: string;
}

/** Remetente dos e-mails do sistema. O parceiro (white label) pode ter o seu. */
export interface PlatformSender {
  fromEmail: string;
  fromName: string;
  partners: PartnerSender[];
}

export interface PlatformConfig {
  emails: SystemEmail[];
  pushes: SystemPush[];
  sender: PlatformSender;
}

/** Texto com os exemplos no lugar das variáveis, para a prévia. */
export function fillVariables(text: string, samples: Record<string, string>): string {
  return text.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (token, key: string) => samples[key] ?? token);
}

export const VARIABLE_SAMPLES: Record<string, string> = {
  loja: 'Sua loja - Centro',
  nome: 'Ana Souza',
  primeiro_nome: 'Ana',
  titulo: 'Pontos adicionados',
  mensagem: '120 pontos foram adicionados à sua carteira',
  quantidade: '120',
  unidade: 'pontos',
  saldo: '1.240',
  codigo: '482913',
  link: 'https://fixe.club/c/abc123',
  arquivo: 'base-2026-10-27.csv',
  assunto: 'Exportação da base',
  dias: '15',
  // Casca do e-mail de sistema: cores e logo do passbook, links fixos do legado.
  cor_fundo: '#1a1aff',
  cor_texto: '#ffffff',
  logo: 'https://front.epossible.com.br/public/assets/img/logo.png',
  link_optout: 'https://slf.vc/optout/abc123',
  link_ajuda: 'https://wa.me/5511970233978?text=Ol%C3%A1%2C+vim+pelo+email+da+Fixe',
  // Pesquisa transacional: `{{bloco_pesquisa}}` é montado pela plataforma (survey-block.ts);
  // o link vem de `idSurvey` + CPF.
  link_pesquisa: 'https://system.epossible.com.br/surveys/abc123?identifier=00000000000',
};
